'use client'

import { useEffect, useLayoutEffect, useState, type CSSProperties } from 'react'

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

const PWA_INSTALLED_KEY = 'pwa-installed'
const PWA_DISMISS_KEY = 'pwa-install-dismissed'

function readFlag(key: string): boolean {
  try {
    return localStorage.getItem(key) === 'true'
  } catch {
    return false
  }
}

function setFlag(key: string): void {
  try {
    localStorage.setItem(key, 'true')
  } catch {
    /* private mode / storage errors */
  }
}

function takeDeferredFromWindow(): BeforeInstallPromptEvent | null {
  if (typeof window === 'undefined') return null
  const w = window as Window & { __galliumDeferredInstall?: Event | null }
  const e = w.__galliumDeferredInstall
  if (!e || typeof (e as BeforeInstallPromptEvent).prompt !== 'function') return null
  w.__galliumDeferredInstall = null
  return e as BeforeInstallPromptEvent
}

function isIOSDevice(): boolean {
  if (typeof navigator === 'undefined') return false
  const ua = navigator.userAgent || ''
  const platform = navigator.platform || ''
  const maxTouchPoints = navigator.maxTouchPoints || 0
  const isIpadOs = platform === 'MacIntel' && maxTouchPoints > 1
  return /iPhone|iPad|iPod/i.test(ua) || isIpadOs
}

function isSupportedMobileOrTabletDevice(): boolean {
  if (typeof navigator === 'undefined') return false
  const ua = navigator.userAgent || ''

  if (isIOSDevice()) return true

  const isAndroid = /Android/i.test(ua)
  const isAndroidTablet = isAndroid && !/Mobile/i.test(ua)
  const isGenericTablet = /Tablet|PlayBook|Silk|Kindle/i.test(ua)
  const isGenericPhone = /Mobi|Mobile|Phone|IEMobile|Opera Mini|Windows Phone/i.test(ua)

  return isAndroid || isAndroidTablet || isGenericTablet || isGenericPhone
}

function isStandalone(): boolean {
  if (typeof window === 'undefined') return false
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as Navigator & { standalone?: boolean }).standalone === true
  )
}

const sheetStyle: CSSProperties = {
  position: 'fixed',
  bottom: '1.5rem',
  left: '50%',
  transform: 'translateX(-50%)',
  width: 'calc(100% - 2rem)',
  maxWidth: '420px',
  background: '#fff',
  borderRadius: '16px',
  boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
  padding: '1.25rem 1.5rem',
  zIndex: 9999,
  display: 'flex',
  flexDirection: 'column',
  gap: '0.75rem',
  border: '1px solid #eee',
}

const primaryBtnStyle: CSSProperties = {
  flex: 1,
  padding: '0.65rem',
  background: '#000',
  color: '#fff',
  border: 'none',
  borderRadius: '10px',
  fontWeight: 600,
  fontSize: '0.95rem',
  cursor: 'pointer',
}

const secondaryBtnStyle: CSSProperties = {
  flex: 1,
  padding: '0.65rem',
  background: '#f3f3f3',
  color: '#333',
  border: 'none',
  borderRadius: '10px',
  fontWeight: 600,
  fontSize: '0.95rem',
  cursor: 'pointer',
}

export default function PWAInstallPrompt() {
  const [mounted, setMounted] = useState(false)
  const [isIOS, setIsIOS] = useState(false)
  const [showPrompt, setShowPrompt] = useState(false)
  const [manualHint, setManualHint] = useState(false)
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null)

  useEffect(() => {
    setMounted(true)
  }, [])

  useLayoutEffect(() => {
    if (!mounted) return
    const captured = takeDeferredFromWindow()
    if (captured) setDeferredPrompt(captured)
  }, [mounted])

  useEffect(() => {
    if (!mounted) return

    const onIOS = isIOSDevice()
    const isSupportedDevice = isSupportedMobileOrTabletDevice()
    setIsIOS(onIOS)

    if (!isSupportedDevice) return

    // Skip prompt when app is installed or user dismissed earlier.
    if (isStandalone()) return
    if (readFlag(PWA_INSTALLED_KEY)) return
    if (readFlag(PWA_DISMISS_KEY)) return

    if (onIOS) {
      const timer = window.setTimeout(() => setShowPrompt(true), 1200)
      return () => window.clearTimeout(timer)
    }

    // Android / Chromium: keep beforeinstallprompt flow.
    const handler = (e: Event) => {
      e.preventDefault()
      setDeferredPrompt(e as BeforeInstallPromptEvent)
    }
    window.addEventListener('beforeinstallprompt', handler)

    const timer = window.setTimeout(() => setShowPrompt(true), 5000)
    return () => {
      window.removeEventListener('beforeinstallprompt', handler)
      window.clearTimeout(timer)
    }
  }, [mounted])

  useEffect(() => {
    if (!mounted) return
    const onInstalled = () => {
      setFlag(PWA_INSTALLED_KEY)
      setShowPrompt(false)
    }
    window.addEventListener('appinstalled', onInstalled)
    return () => window.removeEventListener('appinstalled', onInstalled)
  }, [mounted])

  const handleInstall = async () => {
    if (!deferredPrompt) {
      setManualHint(true)
      return
    }

    try {
      await deferredPrompt.prompt()
      const { outcome } = await deferredPrompt.userChoice
      if (outcome === 'accepted') setFlag(PWA_INSTALLED_KEY)
      setShowPrompt(false)
      setDeferredPrompt(null)
    } catch {
      setManualHint(true)
    }
  }

  const handleGotIt = () => {
    setFlag(PWA_DISMISS_KEY)
    setShowPrompt(false)
  }

  if (!mounted || !showPrompt) return null

  return (
    <div style={sheetStyle} role="dialog" aria-labelledby="pwa-install-title">
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
        <img
          src="/launcher/ic_launcher-xxhdpi.png"
          alt="Gallium Filters"
          width={48}
          height={48}
          style={{ width: 48, height: 48, borderRadius: 10 }}
        />
        <div>
          <div id="pwa-install-title" style={{ fontWeight: 700, fontSize: '1rem' }}>
            {isIOS ? 'Add to Home Screen' : 'Install Gallium Filters'}
          </div>
          <div style={{ fontSize: '0.825rem', color: '#666' }}>
            {isIOS ? 'Install on iPhone/iPad in one step from Share menu.' : 'Add to your home screen for faster access.'}
          </div>
        </div>
      </div>

      {isIOS ? (
        <p style={{ margin: 0, fontSize: '0.85rem', color: '#333', lineHeight: 1.45 }}>
          Tap <strong style={{ fontSize: '1rem' }}>􀈂</strong> <strong>Share</strong>, then choose{' '}
          <strong>Add to Home Screen</strong>.
        </p>
      ) : (
        manualHint && (
          <p style={{ margin: 0, fontSize: '0.8rem', color: '#444', lineHeight: 1.45 }}>
            Tap menu <strong>⋮</strong> → <strong>Install app</strong> or <strong>Add to Home screen</strong>.
          </p>
        )
      )}

      <div style={{ display: 'flex', gap: '0.75rem' }}>
        {!isIOS && (
          <button type="button" onClick={handleInstall} style={primaryBtnStyle}>
            Install
          </button>
        )}
        <button type="button" onClick={handleGotIt} style={secondaryBtnStyle}>
          Got it
        </button>
      </div>
    </div>
  )
}
